import {
    DinnerSuggestionsImportantToMeOptionId,
    DinnerSuggestionsMoreOfOptionId,
} from "../preferences/types"

export const dinnerSuggestionsStepOrder = ["intro", "moreOf", "importantToMe", "consent"] as const

export type DinnerSuggestionsStep = (typeof dinnerSuggestionsStepOrder)[number]

export interface DinnerSuggestionsFlowState {
    currentStep: DinnerSuggestionsStep
    moreOf: DinnerSuggestionsMoreOfOptionId[]
    importantToMe: DinnerSuggestionsImportantToMeOptionId[]
    submitting: boolean
}

export type DinnerSuggestionsPreferenceSelections = Pick<
    DinnerSuggestionsFlowState,
    "moreOf" | "importantToMe"
>

export type DinnerSuggestionsFlowAction =
    | { type: "GO_TO_NEXT_STEP" }
    | { type: "GO_TO_PREVIOUS_STEP" }
    | { type: "SKIP_CURRENT_STEP" }
    | { type: "GO_TO_STEP"; value: DinnerSuggestionsStep }
    | {
          type: "INITIALIZE_PREFERENCES"
          value: Pick<DinnerSuggestionsFlowState, "moreOf" | "importantToMe">
      }
    | { type: "TOGGLE_MORE_OF"; value: DinnerSuggestionsMoreOfOptionId }
    | { type: "TOGGLE_IMPORTANT_TO_ME"; value: DinnerSuggestionsImportantToMeOptionId }
    | { type: "SET_SUBMITTING"; value: boolean }
    | { type: "RESET" }

export const initialDinnerSuggestionsFlowState: DinnerSuggestionsFlowState = {
    currentStep: "intro",
    moreOf: [],
    importantToMe: [],
    submitting: false,
}

const toggleValue = <TValue extends string>(values: TValue[], value: TValue) => {
    return values.includes(value) ? values.filter(item => item !== value) : [...values, value]
}

export const dinnerSuggestionsFlowReducer = (
    state: DinnerSuggestionsFlowState,
    action: DinnerSuggestionsFlowAction,
): DinnerSuggestionsFlowState => {
    switch (action.type) {
        case "GO_TO_NEXT_STEP": {
            const currentStepIndex = dinnerSuggestionsStepOrder.indexOf(state.currentStep)
            const nextStepIndex = Math.min(
                currentStepIndex + 1,
                dinnerSuggestionsStepOrder.length - 1,
            )

            return {
                ...state,
                currentStep: dinnerSuggestionsStepOrder[nextStepIndex],
            }
        }
        case "GO_TO_PREVIOUS_STEP": {
            const currentStepIndex = dinnerSuggestionsStepOrder.indexOf(state.currentStep)
            const previousStepIndex = Math.max(currentStepIndex - 1, 0)

            return {
                ...state,
                currentStep: dinnerSuggestionsStepOrder[previousStepIndex],
            }
        }
        case "SKIP_CURRENT_STEP": {
            const currentStepIndex = dinnerSuggestionsStepOrder.indexOf(state.currentStep)
            const nextStepIndex = Math.min(
                currentStepIndex + 1,
                dinnerSuggestionsStepOrder.length - 1,
            )

            return {
                ...state,
                currentStep: dinnerSuggestionsStepOrder[nextStepIndex],
                moreOf: [],
                importantToMe: [],
            }
        }
        case "GO_TO_STEP":
            return {
                ...state,
                currentStep: action.value,
            }
        case "INITIALIZE_PREFERENCES":
            return {
                ...state,
                currentStep: "intro",
                submitting: false,
                moreOf: action.value.moreOf,
                importantToMe: action.value.importantToMe,
            }
        case "TOGGLE_MORE_OF":
            return {
                ...state,
                moreOf: toggleValue(state.moreOf, action.value),
            }
        case "TOGGLE_IMPORTANT_TO_ME":
            return {
                ...state,
                importantToMe: toggleValue(state.importantToMe, action.value),
            }
        case "SET_SUBMITTING":
            return {
                ...state,
                submitting: action.value,
            }
        case "RESET":
            return initialDinnerSuggestionsFlowState
        default:
            return state
    }
}

export const getDinnerSuggestionsStepIndex = (step: DinnerSuggestionsStep) => {
    return dinnerSuggestionsStepOrder.indexOf(step)
}

export const getDinnerSuggestionsCanContinue = (state: DinnerSuggestionsFlowState) => {
    switch (state.currentStep) {
        case "intro":
            return true
        case "moreOf":
            return state.moreOf.length > 0
        case "importantToMe":
            return state.importantToMe.length > 0
        case "consent":
            return true
        default:
            return false
    }
}

export const getDinnerSuggestionsPrimaryButtonText = (
    step: DinnerSuggestionsStep,
    isSubmitting: boolean,
    shouldShowConsentStep: boolean,
) => {
    if (step === "consent") {
        return isSubmitting ? "Lagrer..." : "Fullfør"
    }

    if (step === "importantToMe") {
        return shouldShowConsentStep ? "Neste steg" : "Fullfør"
    }

    return step === "intro" ? "Kom i gang" : "Neste"
}

export const getDinnerSuggestionsHeaderTitle = (step: DinnerSuggestionsStep) => {
    switch (step) {
        case "moreOf":
            return "Hva vil du ha mer av?"
        case "importantToMe":
            return "Hva er viktig for deg?"
        case "consent":
            return "Personalisering ved bruk av analytiske modeller"
        default:
            return undefined
    }
}
